/**
 * FlowScaffold — shared chrome for all booking flow screens.
 *
 * Values here are matched pixel-for-pixel against the Claude Design
 * prototype (screens.jsx / flow.jsx). Do not change values without
 * comparing against the design source.
 */

import React, { useState, useEffect, useRef, type ReactNode } from 'react';
import { BookingTheme, FLOW_DEFAULTS, FLOW_SPRING, FLOW_COLUMN_MAX } from './themes';

// ─── Types ───────────────────────────────────────────────────────────

export interface BuildRowData {
  label: string;
  value?: string;
  sub?: string;
  state: 'filled' | 'active';
}

// ─── CSS variable refs (with fallback defaults) ─────────────────────

const V = {
  bg:       `var(--f-bg, ${FLOW_DEFAULTS.bg})`,
  ink:      `var(--f-ink, ${FLOW_DEFAULTS.ink})`,
  inkSoft:  `var(--f-ink-soft, ${FLOW_DEFAULTS.inkSoft})`,
  inkFaint: `var(--f-ink-faint, ${FLOW_DEFAULTS.inkFaint})`,
  hairline: `var(--f-hairline, ${FLOW_DEFAULTS.hairline})`,
  surface:  `var(--f-surface, ${FLOW_DEFAULTS.surface})`,
  selBg:    `var(--f-sel-bg, ${FLOW_DEFAULTS.selBg})`,
  selFg:    `var(--f-sel-fg, ${FLOW_DEFAULTS.selFg})`,
} as const;

// ─── ScreenStack (Apple-style push/pop transitions) ─────────────────
// Matched against flow.jsx ScreenStack: the outgoing screen parallaxes
// left and dims while the incoming one slides in from the right
// (reversed on back). iOS easing cubic-bezier(0.32, 0.72, 0, 1).
// The current screen renders live; the leaving screen is a frozen
// snapshot for the ~0.46s transition.

const SCREEN_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SCREEN_DURATION = 460;

const screenKeyframes = `
@keyframes iosPushIn  { from { transform: translateX(100%); }            to { transform: translateX(0); } }
@keyframes iosPushOut { from { transform: translateX(0); opacity: 1; }   to { transform: translateX(-24%); opacity: 0.55; } }
@keyframes iosPopIn   { from { transform: translateX(-24%); opacity: 0.55; } to { transform: translateX(0); opacity: 1; } }
@keyframes iosPopOut  { from { transform: translateX(0); }               to { transform: translateX(100%); } }
`;

let keyframesInjected = false;
function ensureKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = screenKeyframes;
  document.head.appendChild(style);
  keyframesInjected = true;
}

interface ScreenTransition {
  prevKey: string;
  prevNode: ReactNode;
  dir: 'forward' | 'back';
  token: number;
}

// Monotonic token distinguishing successive transitions (render-safe,
// unlike Math.random())
let transitionToken = 0;

export function ScreenStack({ screenKey, order, children, noSnapshotKeys }: {
  screenKey: string;
  order: number;
  children: ReactNode;
  /** Screen keys that must never be kept as an outgoing snapshot (e.g. a
      payment step with Stripe/Turnstile — remounting it for the transition
      would briefly duplicate live payment components). A themed backdrop
      slides out in its place. */
  noSnapshotKeys?: string[];
}) {
  const curRef = useRef({ key: screenKey, order, node: children });
  const transRef = useRef<ScreenTransition | null>(null);
  const [, force] = useState(0);

  useEffect(() => { ensureKeyframes(); }, []);

  // Set up the transition synchronously during render (not in an effect)
  // so the incoming screen is animated from its very first paint — no
  // one-frame flash at rest.
  if (screenKey !== curRef.current.key) {
    const dir: 'forward' | 'back' = order >= curRef.current.order ? 'forward' : 'back';
    const blockSnapshot = noSnapshotKeys?.includes(curRef.current.key);
    transRef.current = {
      prevKey: curRef.current.key,
      prevNode: blockSnapshot
        ? <div style={{ position: 'absolute', inset: 0, background: `var(--f-bg, ${FLOW_DEFAULTS.bg})` }} />
        : curRef.current.node,
      dir,
      token: ++transitionToken,
    };
    curRef.current = { key: screenKey, order, node: children };
  } else {
    curRef.current.node = children; // keep snapshot fresh
  }

  const trans = transRef.current;
  const tok = trans ? trans.token : null;
  useEffect(() => {
    if (tok == null) return;
    const id = setTimeout(() => { transRef.current = null; force((x) => x + 1); }, SCREEN_DURATION + 10);
    return () => clearTimeout(id);
  }, [tok]);

  const dir = trans ? trans.dir : 'forward';
  const curOnTop = dir === 'forward';

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {trans && (
        <div
          key={'prev-' + trans.prevKey}
          aria-hidden="true"
          // React 18 doesn't support the boolean `inert` prop — set the
          // attribute imperatively so the outgoing screen can't receive
          // focus or be reached by assistive technology during the ~460ms
          // transition (it's a live subtree, not a static snapshot)
          ref={(el) => { if (el) el.setAttribute('inert', ''); }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: curOnTop ? 0 : 1,
            pointerEvents: 'none',
            animation: `${dir === 'forward' ? 'iosPushOut' : 'iosPopOut'} ${SCREEN_DURATION}ms ${SCREEN_EASING} both`,
          }}
        >
          {trans.prevNode}
        </div>
      )}
      <div
        key={'cur-' + screenKey}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: curOnTop ? 1 : 0,
          animation: trans
            ? `${dir === 'forward' ? 'iosPushIn' : 'iosPopIn'} ${SCREEN_DURATION}ms ${SCREEN_EASING} both`
            : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── SectionLabel — eyebrow label used across flow steps ────────────

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontSize: 10,
      textTransform: 'uppercase',
      letterSpacing: '0.22em',
      color: V.inkFaint,
      fontWeight: 600,
    }}>{children}</div>
  );
}

// ─── BuildRow ────────────────────────────────────────────────────────

function BuildRow({ theme, row, last }: {
  theme: BookingTheme;
  row: BuildRowData;
  last: boolean;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 14,
      padding: '12px 0',
      borderBottom: last ? 'none' : `0.5px solid ${theme.border}`,
    }}>
      <span style={{
        fontSize: 9.5,
        textTransform: 'uppercase',
        letterSpacing: '0.18em',
        fontWeight: 600,
        color: theme.fg,
        opacity: 0.5,
        flexShrink: 0,
      }}>{row.label}</span>

      {row.value != null && (
        <span style={{ textAlign: 'right', flex: '1 1 auto', minWidth: 0 }}>
          <span style={{
            display: 'block',
            fontFamily: theme.display,
            fontStyle: theme.displayItalic ? 'italic' : 'normal',
            fontWeight: theme.displayWeight,
            fontSize: 17,
            lineHeight: 1.15,
            color: theme.fg,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>{row.value}</span>
          {row.sub && (
            <span style={{
              display: 'block',
              fontSize: 11,
              color: theme.fg,
              opacity: 0.6,
              marginTop: 2,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}>{row.sub}</span>
          )}
        </span>
      )}
    </div>
  );
}

// ─── BuildPass ───────────────────────────────────────────────────────

export function BuildPass({ theme, rows }: {
  theme: BookingTheme;
  rows: BuildRowData[];
}) {
  if (rows.length === 0) return null;

  return (
    <div style={{
      background: theme.bg,
      color: theme.fg,
      borderRadius: 18,
      padding: '6px 18px 8px',
      boxShadow: '0 8px 22px rgba(28,18,12,0.14)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        {rows.map((r, i) => (
          <BuildRow
            key={r.label}
            theme={theme}
            row={r}
            last={i === rows.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

// ─── FlowTopBar ──────────────────────────────────────────────────────

export function FlowTopBar({ step, totalSteps, theme, businessName, onBack }: {
  step: number;
  totalSteps: number;
  theme: BookingTheme;
  businessName: string;
  onBack: () => void;
}) {
  const progress = step / totalSteps;

  return (
    <div style={{
      paddingTop: 16,
      paddingBottom: 14,
      paddingLeft: 18,
      paddingRight: 18,
      position: 'relative',
      flexShrink: 0,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: FLOW_COLUMN_MAX - 36,
        margin: '0 auto',
      }}>
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 38,
            height: 38,
            borderRadius: 19,
            background: V.surface,
            border: `0.5px solid ${V.hairline}`,
            boxShadow: '0 1px 2px rgba(28,18,12,0.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
            <path
              d="M8 2.5L3.5 6.5L8 10.5"
              stroke={V.ink}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Center: brand identity */}
        <div style={{
          textAlign: 'center',
          minWidth: 0,
          flex: 1,
          padding: '0 8px',
        }}>
          <div style={{
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: '0.22em',
            color: V.inkFaint,
            fontWeight: 600,
          }}>Booking with</div>
          <div style={{
            fontFamily: theme.display,
            fontStyle: theme.displayItalic ? 'italic' : 'normal',
            fontWeight: theme.displayWeight,
            fontSize: 15.5,
            marginTop: 2,
            letterSpacing: '-0.01em',
            color: V.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>{businessName}</div>
        </div>

        {/* Step counter */}
        <div style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 11,
          fontWeight: 500,
          color: V.inkSoft,
          letterSpacing: '0.04em',
          minWidth: 48,
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}>
          {String(step).padStart(2, '0')}
          <span style={{ color: V.inkFaint }}> / </span>
          {String(totalSteps).padStart(2, '0')}
        </div>
      </div>

      {/* Progress hairline */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: `max(24px, calc(50% - ${FLOW_COLUMN_MAX / 2 - 24}px))`,
        right: `max(24px, calc(50% - ${FLOW_COLUMN_MAX / 2 - 24}px))`,
        height: 1,
        background: V.hairline,
      }}>
        <div style={{
          height: '100%',
          width: `${progress * 100}%`,
          background: V.inkSoft,
          transition: `width 600ms ${FLOW_SPRING}`,
        }} />
      </div>
    </div>
  );
}

// ─── FlowDock ────────────────────────────────────────────────────────

export function FlowDock({ cta, ctaDisabled, ctaSub, onCta }: {
  cta: string;
  ctaDisabled?: boolean;
  ctaSub?: string;
  onCta: () => void;
}) {
  return (
    <div style={{
      flexShrink: 0,
      padding: '14px 24px',
      paddingBottom: 'calc(34px + env(safe-area-inset-bottom, 0px))',
      background: `linear-gradient(180deg, transparent 0%, ${V.bg} 28%)`,
      position: 'relative',
    }}>
      {ctaSub && (
        <div style={{
          textAlign: 'center',
          marginBottom: 10,
          fontSize: 12,
          color: V.inkSoft,
          letterSpacing: '-0.005em',
        }}>{ctaSub}</div>
      )}
      <button
        type="button"
        onClick={ctaDisabled ? undefined : onCta}
        disabled={!!ctaDisabled}
        style={{
          width: '100%',
          height: 56,
          borderRadius: 999,
          border: 'none',
          background: ctaDisabled ? 'rgba(28,18,12,0.16)' : V.selBg,
          color: ctaDisabled ? 'rgba(255,255,255,0.6)' : V.selFg,
          fontFamily: 'inherit',
          fontSize: 15.5,
          fontWeight: 500,
          letterSpacing: '-0.01em',
          cursor: ctaDisabled ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          transition: 'background 240ms ease, color 240ms ease',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {cta}
        {!ctaDisabled && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 7h8M7.5 3.5L11 7L7.5 10.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

// ─── FlowScaffold ────────────────────────────────────────────────────

export function FlowScaffold({
  step,
  totalSteps,
  theme,
  businessName,
  onBack,
  title,
  subtitle,
  children,
  cta,
  ctaDisabled,
  ctaSub,
  onCta,
  buildRows,
}: {
  step: number;
  totalSteps: number;
  theme: BookingTheme;
  businessName: string;
  onBack: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  cta: string;
  ctaDisabled?: boolean;
  ctaSub?: string;
  onCta: () => void;
  buildRows?: BuildRowData[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasBuildRows = buildRows && buildRows.length > 0;

  // Reset scroll on step change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [step]);

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      background: V.bg,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Inter Tight", system-ui, sans-serif',
      color: V.ink,
    }}>
      {/* Top chrome */}
      <FlowTopBar
        step={step}
        totalSteps={totalSteps}
        theme={theme}
        businessName={businessName}
        onBack={onBack}
      />

      {/* Scrollable body */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '8px 24px 0',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Build-pass ticket stub */}
        {hasBuildRows && (
          <div style={{ paddingTop: 4, marginBottom: 22 }}>
            <BuildPass theme={theme} rows={buildRows} />
          </div>
        )}

        {/* Page header */}
        <div style={{ paddingTop: 6 }}>
          <div style={{
            fontFamily: theme.display,
            fontStyle: theme.displayItalic ? 'italic' : 'normal',
            fontWeight: theme.displayWeight,
            fontSize: hasBuildRows ? 30 : 38,
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            color: V.ink,
          }}>{title}</div>
          {subtitle && !hasBuildRows && (
            <div style={{
              marginTop: 12,
              fontSize: 13.5,
              lineHeight: 1.45,
              color: V.inkSoft,
              letterSpacing: '-0.005em',
              maxWidth: 320,
            }}>{subtitle}</div>
          )}
        </div>

        {/* Content */}
        <div style={{ paddingTop: 28, paddingBottom: 24 }}>
          {children}
        </div>
      </div>

      {/* Bottom CTA dock */}
      <FlowDock
        cta={cta}
        ctaDisabled={ctaDisabled}
        ctaSub={ctaSub}
        onCta={onCta}
      />
    </div>
  );
}
